import React, { useEffect, useRef, useState } from 'react';
import { X, Camera, Upload, Search, AlertCircle } from 'lucide-react';
import { useProducts } from '@/store/dataStore';
import { Link } from 'react-router-dom';
import { thumb } from '@/lib/img';

// PhotoSearch now uses Gemini Vision via /api/products/visual-search; the
// legacy color-signature helpers were removed. Barcode scanner below is
// unchanged and still uses html5-qrcode.

function BarcodeScanner({ onDetected, onClose }) {
  const containerRef = useRef(null);
  const scannerRef = useRef(null);
  const startedRef = useRef(false);
  const stopRequestedRef = useRef(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    (async () => {
      try {
        const { Html5Qrcode, Html5QrcodeSupportedFormats } = await import('html5-qrcode');
        if (stopRequestedRef.current) return;
        const scanner = new Html5Qrcode(containerRef.current.id, {
          formatsToSupport: [
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.UPC_A,
            Html5QrcodeSupportedFormats.UPC_E,
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.CODE_39,
            Html5QrcodeSupportedFormats.QR_CODE,
          ],
        });
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 15, qrbox: { width: 260, height: 160 } },
          (decoded) => {
            if (stopRequestedRef.current) return;
            scanner.stop().catch(() => {});
            onDetected(decoded);
          },
          () => {}
        );
        startedRef.current = true;
        // Unmount may have happened while we were awaiting start()
        if (stopRequestedRef.current) {
          await scanner.stop().catch(() => {});
        }
      } catch (e) { setErr(e.message || 'No pudimos acceder a la cámara. Otorga permisos.'); }
    })();
    return () => {
      stopRequestedRef.current = true;
      if (startedRef.current) {
        scannerRef.current?.stop().catch(() => {}).then(() => scannerRef.current?.clear().catch(() => {}));
      }
    };
    // eslint-disable-next-line
  }, []);
  return (
    <div>
      <div id="barcode-region" ref={containerRef} className="w-full aspect-video bg-slate-900 rounded-lg overflow-hidden" data-testid="barcode-region" />
      {err && <p className="mt-2 text-sm text-red-600 flex items-center gap-1"><AlertCircle size={14} /> {err}</p>}
      <p className="mt-2 text-[11px] text-slate-500 text-center">Alinea el código de barras dentro del recuadro. Funciona con EAN, UPC, Code 128 y QR.</p>
    </div>
  );
}

function PhotoSearch({ onClose, onProductPick }) {
  const [previewUrl, setPreviewUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {keywords, category, description, matches}
  const [err, setErr] = useState('');
  const inputRef = useRef(null);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true); setErr(''); setResult(null);
    setPreviewUrl(URL.createObjectURL(file));
    try {
      // Upload to Gemini-powered backend endpoint. Server rate-limits per IP.
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products/visual-search`, { method: 'POST', body: fd });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || `HTTP ${res.status}`);
      setResult(j);
    } catch (ex) { setErr(ex.message || 'Error procesando la imagen'); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <div className="flex flex-col md:flex-row gap-3">
        <div className="md:w-1/3">
          <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFile} data-testid="photo-search-file" />
          <button onClick={() => inputRef.current?.click()} className="w-full aspect-square border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center text-slate-500 hover:border-yellow-400 hover:text-[#1B2A4A] transition" data-testid="btn-choose-photo">
            {previewUrl ? <img src={previewUrl} alt="ref" className="w-full h-full object-cover rounded-xl" /> : (<><Upload size={28} className="mb-2" /><span className="text-xs">Elegir foto o tomarla</span></>)}
          </button>
          <p className="mt-2 text-[11px] text-slate-500 text-center">IA analiza el producto (marca, tipo, color) y busca coincidencias.</p>
        </div>
        <div className="flex-1">
          {busy && <div className="text-sm text-slate-500 text-center py-6">Analizando con IA…</div>}
          {err && <p className="text-sm text-red-600 flex items-center gap-1"><AlertCircle size={14} />{err}</p>}
          {!result && !busy && !err && <div className="text-sm text-slate-400 text-center py-8">Sube una foto o toma una en vivo para que Lidia identifique el producto.</div>}
          {result && (
            <div className="space-y-2">
              {result.description && <p className="text-sm text-slate-700 italic">"{result.description}"</p>}
              <div className="flex flex-wrap gap-1.5">
                {(result.keywords || []).map((k) => (
                  <span key={k} className="bg-yellow-100 text-[#1B2A4A] rounded-full px-2 py-0.5 text-[11px] font-semibold" data-testid={`vs-kw-${k}`}>#{k}</span>
                ))}
                {result.category && <span className="bg-[#1B2A4A] text-yellow-300 rounded-full px-2 py-0.5 text-[11px] font-semibold">▸ {result.category}</span>}
              </div>
              <p className="text-xs text-slate-500 pt-1 pb-0.5">Coincidencias: <b>{result.matches?.length || 0}</b> / {result.total_scanned}</p>
              {result.matches?.length === 0 ? (
                <div className="text-sm text-slate-400 text-center py-6 bg-slate-50 rounded-xl">Sin coincidencias en el catálogo — pide asesoría al Super Admin.</div>
              ) : (
                <div className="grid grid-cols-3 gap-2 max-h-80 overflow-y-auto">
                  {(result.matches || []).map((p) => (
                    <Link key={p.id} to={`/producto/${p.id}`} onClick={onClose} className="border rounded-lg overflow-hidden hover:shadow" data-testid={`photo-result-${p.id}`}>
                      {p.image && <img src={thumb(p.image, 400)} alt={p.name} className="w-full aspect-square object-cover" loading="lazy" />}
                      <div className="p-1.5 text-[11px] text-center">
                        <div className="font-bold truncate" title={p.name}>{p.name}</div>
                        <div className="text-slate-500">${(p.price || 0).toFixed(2)}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function VisualSearchModal({ mode, onClose, onResult }) {
  const { products } = useProducts();
  const handleBarcode = (code) => {
    const c = (code || '').trim();
    if (!c) return;
    const match = products.find((p) => p.barcode === c || p.internal_code === c || p.id === c);
    if (match) onResult(match.name); else onResult(c);
  };
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full p-4 relative max-h-[92vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-2 right-2 text-slate-400 hover:text-slate-700" data-testid="btn-close-visual"><X size={20} /></button>
        <h3 className="font-bold text-[#1B2A4A] flex items-center gap-2 mb-3">
          {mode === 'barcode' ? <><Camera size={18} /> Escanear código de barras</> : <><Search size={18} /> Buscar por foto</>}
        </h3>
        {mode === 'barcode' ? (
          <BarcodeScanner onDetected={handleBarcode} onClose={onClose} />
        ) : (
          <PhotoSearch onClose={onClose} onProductPick={(name) => onResult(name)} />
        )}
      </div>
    </div>
  );
}
